/**
 * ONE CLOCK FOR EVERY LOADER ON THE PAGE.
 *
 * The loader's motion is a CSS animation, and a CSS animation starts from zero whenever its
 * element is created. Between a reload and a drawn page the mark is created three times, as the
 * splash in `index.html`, the boot `Splash`, and the page's own loader, so each handover restarted
 * the animation and the mark visibly jumped back to empty. That, and nothing in the animation
 * itself, was the jerk on reload.
 *
 * So every loader is started in the middle of the loop, where the loop already is: `loaderPhase`
 * is how far through the cycle the page's clock stands, `BrandLoader` hands it to the animation as
 * a negative delay, and a loader created a second later picks up the beat of the one it replaced.
 * The clock's zero is the moment the splash in `index.html` began, adopted once at boot.
 *
 * `LOADER_CYCLE_MS` is the animation's duration in `index.html`'s `#lm-loader-css`, and a test holds
 * the two equal.
 */

export const LOADER_CYCLE_MS = 2200;

/**
 * How long a cold load shows the loader at least. A reload from cache puts the app on screen in
 * about a tenth of a second, which is under one frame of the loader's own motion, so the loader
 * was never seen at all, only flashed. 1.5 s lets it draw, fill and glow once. Measured from the
 * start of the navigation, so a slow load adds nothing to it.
 */
export const BOOT_MIN_MS = 1500;

/** The hand-over: the loader fades while the page appears beneath it. It ends while the mark is
 *  still fully filled (the loop starts to unwind at 82% of 2.2 s, which is 1.8 s). */
export const BOOT_FADE_MS = 300;

let origin = 0;

/** Where the loop stands, in ms from its start, at time `now` for a loop begun at `from`. */
export function phaseAt(now: number, from: number, cycle: number = LOADER_CYCLE_MS): number {
  const t = (now - from) % cycle;
  return t < 0 ? t + cycle : t;
}

/**
 * Called once, before React renders: take the splash's own start as the clock's zero, so the first
 * loader React draws continues the one the HTML painted. Without a running splash (reduced motion,
 * or a browser without the Web Animations API) the clock starts at the navigation, and a loader
 * simply starts in step with it.
 */
export function adoptBootClock(): void {
  if (typeof document === 'undefined' || typeof document.getAnimations !== 'function') return;
  const splash = document
    .getAnimations()
    .find((a) => (a as CSSAnimation).animationName === 'lm-mark-draw');
  if (splash && typeof splash.startTime === 'number') origin = splash.startTime;
}

/** The loop's position now, for a loader that is being created. */
export function loaderPhase(): number {
  return phaseAt(performance.now(), origin);
}

/** For a test: put the clock back where a fresh page has it. */
export function resetLoaderClock(): void {
  origin = 0;
}
